import { FC, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  getApprovalTimesheetThunk,
  getCandidatesThunk,
} from "../../../store/timesheet/thunks";
import { ITimesheetData, TTimesheetSort } from "../../../store/timesheet/types";
import { SortOrder } from "../../../store/vacancy/types";
import PaginationControls from "../../Pagination/PaginationControls";
import SaveNotification from "../../SaveNotification";
import SearchAndFilter from "../../SuperAdmin/Timesheet/common/SearchAndFilter";
import TableHeader from "../../SuperAdmin/Timesheet/common/TableHeader";
import TableRow from "../../SuperAdmin/Timesheet/common/TableRow";
import TabsHeader from "../../SuperAdmin/Timesheet/common/TabsHeader";
import PopupModal from "../../UI/Popup";
import ReviewTimesheet from "./ReviewTimesheet";

export type TSuccessMsg = "Submitted" | "Done" | null;

const Timesheet: FC = () => {
  const dispatch = useDispatch();

  const { approvalTimesheets, approvalPagination } = useSelector(
    (state) => state.timesheet
  );

  const [selectedTimesheet, setSelectedTimesheet] =
    useState<ITimesheetData | null>(null);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [successMsg, setSucessMsg] = useState<TSuccessMsg>(null);

  // Local states for sortin
  const [sortBy, setSortBy] = useState<TTimesheetSort>("first_name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  // Search and debouncing states
  const [keyword, setKeyword] = useState("");
  const [selectedCentreIds, setSelectedCentreIds] = useState<number[]>([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>(
    []
  );

  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);

  // Handle sorting logic
  const handleSortChange = (field: TTimesheetSort) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  const handleReview = async (timesheet: ITimesheetData) => {
    setSelectedTimesheet(timesheet);
    setShowReviewModal(true);
  };
  const onClickClose = () => {
    setSelectedTimesheet(null);
    setShowReviewModal(false);
  };

  const fetchApprovalsTImesheets = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());

    try {
      // If the candidate list is empty, fetch it
      // if (allCandidatesList.length === 0) {
      await dispatch(getCandidatesThunk({ type: "pending_approvals" }));
      // }

      // Now, make the timesheet request
      await dispatch(
        getApprovalTimesheetThunk({
          searchValue: keyword,
          page: page,
          per_page: perPage,
          sort_by: sortBy,
          sort_order: sortOrder,
          centres: selectedCentreIds,
          candidates: selectedCandidateIds,
        })
      );
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  }, [
    dispatch,
    keyword,
    selectedCandidateIds,
    selectedCentreIds,
    page,
    perPage,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchApprovalsTImesheets();
  }, [
    fetchApprovalsTImesheets,
    keyword,
    selectedCandidateIds,
    selectedCentreIds,
    page,
    perPage,
    sortBy,
    sortOrder,
  ]);

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);
      setPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);

  const handleActionSuccess = (msg: TSuccessMsg) => {
    setSucessMsg(msg);
    setTimeout(() => {
      dispatch(fetchApprovalsTImesheets);
      setSucessMsg(null);
    }, 3000);
  };



  return (
    <>
      {successMsg && (
        <SaveNotification
          message={successMsg}
          onClose={() => setSucessMsg(null)}
        />
      )}
      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column">
            <TabsHeader role="centre_admin" activeTab="approval" />
            <div className="bg-black w-100 h-5px"></div>
          </div>
          <SearchAndFilter
            variant="client-approval"
            keyword={searchInput}
            selectedCentreIds={selectedCentreIds}
            selectedCandidateIds={selectedCandidateIds}
            onKeywordChange={setSearchInput}
            onCentreIdsChange={setSelectedCentreIds}
            onCandidateIdsChange={setSelectedCandidateIds}
            handleSortChange={handleSortChange}
          />
          <div className="d-flex flex-column">
            <TableHeader
              variant="client-approval"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
            {approvalTimesheets.length > 0 ? (
              approvalTimesheets.map((timesheet, index) => (
                <div key={index}>
                  <TableRow
                    key={timesheet.id}
                    variant="client-approval"
                    btnType={"Review"}
                    timesheet={timesheet}
                    showStatus={!!timesheet?.decline_reason?.message}
                    onReviewClick={() => handleReview(timesheet)}
                  />
                </div>
              ))
            ) : (
              <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                <div className="fs-16 fw-400 text-grey-medium">
                  No approval timesheets found
                </div>
              </div>
            )}
            {/* Render pagination controls if there are more than 10 users */}
            {approvalPagination &&
              approvalPagination?.total > DEFAULT_ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={page} // Current page number
                  totalPages={approvalPagination?.last_page} // Total number of pages
                  paginationLinks={approvalPagination?.links} // List of pagination links
                  onPageChange={handlePageChange} // Function to handle page change
                  setPerPageList={setPerPage} // Function to set the number of items per page
                  setCurrentPage={setPage} // Function to set the current page
                />
              )}
          </div>
        </div>
      </div>
      {/* Timesheet Review Modal */}
      <PopupModal
        visible={showReviewModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        {selectedTimesheet && (
          <ReviewTimesheet
            timesheet={selectedTimesheet}
            onActionSuccess={handleActionSuccess}
            onClickClose={onClickClose}
          />
        )}
      </PopupModal>
    </>
  );
};

export default Timesheet;
